import { useEffect, useRef, useState } from "react";
import { api, money, anyDay } from "../api.js";
import { useResource } from "./useResource.js";
import { Empty } from "./common.jsx";
import { positionsUah, pnRows, cleanName, withMarkup } from "../../../lib/customs/spec.js";

// Митні декларації: завантаження .imfx (файл результату з кабінету Держмитслужби), перелік,
// картка декларації з товарами й специфікацією, звірка з реєстром СЕА — як в etax
// Коди одиниць у специфікації МД — як mdUnit у дашборді etax
const UNITS = { "796": "шт", "798": "тис. шт", "166": "кг", "168": "т", "006": "м", "055": "м²",
  "113": "м³", "112": "л", "715": "пара", "736": "рул", "778": "уп", "839": "компл" };
const unit = (u) => UNITS[u] || u || "";
const STATUS = { R: "Оформлено", O: "Оформлено", N: "Відмовлено" };
const uah = (v) => (v == null ? "—" : `${money(v)} грн`);
const CAN_UPLOAD = ["preparer", "submitter", "owner"];

export default function Customs({ companyId, company, sub, versions }) {
  if (sub) return <Declaration companyId={companyId} mrn={sub} canEdit={CAN_UPLOAD.includes(company.role)} />;
  return <CustomsList companyId={companyId} canUpload={CAN_UPLOAD.includes(company.role)} versions={versions} />;
}

function Upload({ companyId, onDone }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const [drag, setDrag] = useState(false);
  const send = async (files) => {
    const list = [...files].filter((f) => /\.imfx$/i.test(f.name));
    const rejected = [...files].filter((f) => !/\.imfx$/i.test(f.name)).map((f) => ({ name: f.name, tone: "bad", text: "не .imfx — пропущено" }));
    setBusy(true); setLog(rejected);
    const out = [...rejected];
    for (const f of list) {
      try {
        const res = await fetch(`/api/companies/${companyId}/customs/upload?filename=${encodeURIComponent(f.name)}`, {
          method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/vnd.etax.imfx" }, body: f,
        });
        const j = await res.json().catch(() => ({}));
        out.push(res.ok ? { name: f.name, tone: "ok", text: `${j.mrn} · товарів ${j.goods}` }
          : { name: f.name, tone: j.skipped ? "warn" : "bad", text: j.error || `Помилка ${res.status}` });
      } catch (e) {
        out.push({ name: f.name, tone: "bad", text: e.message });
      }
      setLog([...out]);
    }
    setBusy(false);
    onDone();
  };
  return (
    <div className={`card dropzone${drag ? " drag" : ""}`}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); send(e.dataTransfer.files); }}>
      <div className="row">
        <div>
          <b>Завантажити МД</b>
          <p className="muted small">Файли результату .imfx з кабінету Держмитслужби. Перетягніть сюди або оберіть. Враховується імпорт (ІМ 40); повторне завантаження оновлює декларацію.</p>
        </div>
        <button type="button" className="btn btn-sm" onClick={() => input.current?.click()} disabled={busy}>{busy ? "Завантаження…" : "Обрати файли .imfx"}</button>
        <input ref={input} type="file" accept=".imfx" multiple hidden onChange={(e) => { send(e.target.files); e.target.value = ""; }} aria-label="Файли .imfx" />
      </div>
      {log.length > 0 && (
        <ul className="upload-log" aria-live="polite">
          {log.map((x, i) => <li key={i} className={`status status-${x.tone}`}><span className="status-dot" aria-hidden="true" /><span><b>{x.name}</b> — {x.text}</span></li>)}
        </ul>
      )}
    </div>
  );
}

// Підключення до API митниці: інструкція, як отримати токен, поле для нього, перевірка й зберігання (токен — лише на
// сервері, зашифрований; назад не показується). За токеном — отримання МД з митниці (FetchFromCustoms)
const when = (t) => (t ? new Date(t).toLocaleString("uk-UA", { dateStyle: "short", timeStyle: "short" }) : "");
function CustomsConnect({ companyId, canEdit, onFetched }) {
  const url = `/api/companies/${companyId}/customs-api`;
  const [rev, setRev] = useState(0);
  const s = useResource(url, rev);
  const [editing, setEditing] = useState(false);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const field = useRef(null);
  // «Замінити токен» — фокус у поле, щойно форма з'явилась
  useEffect(() => { if (editing) field.current?.focus(); }, [editing]);
  const run = async (name, fn) => {
    setBusy(name); setErr(null);
    try { await fn(); setRev((x) => x + 1); return true; } catch (e) { setErr(e.message); return false; } finally { setBusy(null); }
  };
  const save = async (e) => {
    e.preventDefault();
    if (await run("save", () => api(url, { method: "PUT", body: { token } }))) { setToken(""); setEditing(false); }
  };
  const d = s.data;
  if (s.pending) return null;
  const showForm = canEdit && (!d?.connected || editing);
  const st = d?.connected ? (
    d.checkStatus === "ok" ? <span className="pn-ok">✓ Підключено: митниця прийняла токен{d.checkCount != null ? `, МД за останні ${d.checkDays} днів — ${d.checkCount}` : ""} (перевірено {when(d.checkedAt)})</span>
      : d.checkStatus === "unavailable" ? <span className="notice">Токен збережено, але митниця не відповіла під час перевірки {when(d.checkedAt)}. Перевірте ще раз пізніше.</span>
      : <span className="error">Остання перевірка {when(d.checkedAt)}: {d.checkMessage || "не пройдено"}</span>
  ) : null;
  return (
    <section className="card customs-connect" aria-labelledby="customs-connect-h">
      <h3 id="customs-connect-h" className="block-title">Підключення до митниці</h3>
      <p className="small">Підключіть компанію до API митниці — тоді eCabinet зможе сам отримувати ваші митні декларації, без завантаження файлів .imfx.
        Потрібен лише токен доступу з кабінету митниці; ключ КЕП і пароль сюди не вводяться.</p>
      {d?.connected && <p className="small" role="status">{st}{d.addedBy ? <span className="muted"> · додав {d.addedBy} {when(d.addedAt)}</span> : null}</p>}
      {d?.connected && d.checkStatus !== "rejected" && canEdit && <FetchFromCustoms companyId={companyId} onDone={onFetched} label="Отримати МД з митниці за 60 днів" />}
      {showForm && (
        <>
          <details className="customs-howto" open={!d?.connected}>
            <summary>Як отримати токен</summary>
            <ol className="small">
              <li>Увійдіть у Кабінет митниці — <a href="https://cabinet.customs.gov.ua" target="_blank" rel="noopener noreferrer">cabinet.customs.gov.ua</a> — ключем КЕП компанії або уповноваженої особи.</li>
              <li>Відкрийте розділ «Бізнесу» → «Моя акредитація».</li>
              <li>У блоці «Токен» (поруч — дата створення) натисніть «Копіювати».</li>
              <li>Вставте токен у поле нижче й натисніть «Перевірити й зберегти».</li>
            </ol>
            <p className="muted small">Токен дає лише читання митних декларацій вашої компанії (за поточний і три попередні роки). Перед збереженням ми перевіряємо його
              запитом переліку ваших МД за {d?.checkDays || 30} днів; токен, який митниця не прийняла, не зберігається. Зберігаємо його зашифрованим і назад не показуємо.
              Якщо токен міг потрапити до сторонніх — видаліть його в кабінеті митниці кнопкою «Видалити» в блоці «Токен», отримайте новий і замініть тут.</p>
          </details>
          <form className="customs-token" onSubmit={save}>
            <label className="field">Токен доступу до API митниці
              <input ref={field} type="password" value={token} onChange={(e) => setToken(e.target.value)} autoComplete="off" spellCheck={false}
                aria-describedby={err ? "customs-token-err" : undefined} aria-invalid={err ? true : undefined} required />
            </label>
            <div className="actions">
              <button type="submit" className="btn btn-sm" disabled={!token.trim() || Boolean(busy)}>{busy === "save" ? "Перевіряємо в митниці…" : "Перевірити й зберегти"}</button>
              {d?.connected && <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setEditing(false); setToken(""); setErr(null); }}>Скасувати</button>}
            </div>
          </form>
        </>
      )}
      {err && <p className="error small" id="customs-token-err" role="alert">{err}</p>}
      {canEdit && d?.connected && !editing && (
        <div className="actions">
          <button type="button" className="btn btn-secondary btn-sm" disabled={Boolean(busy)} onClick={() => run("check", () => api(`${url}/check`, { method: "POST" }))}>{busy === "check" ? "Перевіряємо…" : "Перевірити ще раз"}</button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>Замінити токен</button>
          <button type="button" className="btn btn-ghost btn-sm" disabled={Boolean(busy)} onClick={() => { if (confirm("Видалити токен митниці для цієї компанії?")) run("del", () => api(url, { method: "DELETE" })); }}>Видалити</button>
        </div>
      )}
      {!canEdit && !d?.connected && <p className="muted small">Підключати можуть ролі «підготовка», «подання» і «власник».</p>}
    </section>
  );
}

function CustomsList({ companyId, canUpload, versions }) {
  const [reload, setReload] = useState(0);
  const r = useResource(`/api/companies/${companyId}/customs`, `${versions.sea || 0}:${reload}`);
  const d = r.data;
  const list = d?.declarations || [];
  // Рахуються лише МД, відображені в СЕА; решта (напр. оформлені за договором комісії) — лише для довідки
  const counted = d?.seaSynced ? list.filter((x) => x.in_sea) : list, off = list.length - counted.length;
  const missing = d?.missing || [];
  const conn = useResource(`/api/companies/${companyId}/customs-api`, reload);
  const sum = (k) => money(counted.reduce((a, x) => a + Number(x[k] || 0), 0));
  return (
    <>
      <div className="section-head">
        <div>
          <h2>Митні декларації</h2>
          <p className="notice">Імпорт за МД. «У СЕА» — декларація відображена в реєстрі операцій СЕА ПДВ; лише такі входять у податковий кредит, рух товарів і залишки, як в etax. МД, якої в СЕА немає (напр. оформлена за договором комісії на комісіонера), не враховується.</p>
        </div>
      </div>
      {missing.length > 0 && (
        <section className="card attention-inline" role="status" aria-labelledby="md-missing-h">
          <h3 id="md-missing-h" className="block-title">У СЕА ПДВ є {missing.length} МД, не завантажених сюди</h3>
          <p className="small">ПДВ за ними вже враховано з реєстру СЕА, але без файлу МД немає товарів — рух і залишки за кодами УКТЗЕД неповні. Завантажте ці декларації (.imfx або XML з «Митниці»).</p>
          <ul className="small md-missing">{missing.map((m) => <li key={m.md_key}><span className="mono-sm">{m.docnum}</span> від {anyDay(m.docdt)} · ПДВ {money(m.vat)} грн</li>)}</ul>
          {conn.data?.connected && canUpload && <FetchFromCustoms companyId={companyId} onDone={() => setReload((x) => x + 1)} label={missing.length > 1 ? "Отримати ці МД з митниці" : "Отримати цю МД з митниці"}
            range={[addDays(missing.map((m) => String(m.docdt).slice(0, 10)).sort()[0], -7), addDays(missing.map((m) => String(m.docdt).slice(0, 10)).sort().at(-1), 3)]} />}
        </section>
      )}
      <CustomsConnect companyId={companyId} canEdit={canUpload} onFetched={() => setReload((x) => x + 1)} />
      {canUpload ? <Upload companyId={companyId} onDone={() => setReload((x) => x + 1)} />
        : <p className="notice">Завантажувати МД можуть ролі «підготовка», «подання» і «власник».</p>}
      <div className="card">
        {r.pending ? <p className="muted" role="status">Завантаження…</p>
          : r.error && !d ? <p className="error">Не вдалося завантажити: {r.error.message}</p>
          : !list.length ? <Empty>Митних декларацій ще немає.</Empty> : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>MRN</th><th>Тип</th><th>Оформлено</th><th className="num">Митна вартість</th><th className="num">Мито</th><th className="num">ПДВ</th><th>У СЕА</th></tr></thead>
                <tbody>
                  {list.map((x) => {
                    const open = () => { location.hash = `#/c/${companyId}/customs/${encodeURIComponent(x.mrn)}`; };
                    return (
                      <tr key={x.mrn} tabIndex={0} onClick={open} onKeyDown={(e) => { if (e.key === "Enter") open(); }}>
                        <td className="mono-sm">{x.mrn}</td><td>{x.type}</td><td>{anyDay(x.registered)}</td>
                        <td className="num">{money(x.value_total)}</td><td className="num">{money(x.duty_total)}</td><td className="num">{money(x.vat_total)}</td>
                        <td>{x.in_sea ? <span className="badge badge-ok">так</span> : d.seaSynced ? <span className="badge badge-idle" title="Не враховується: МД немає в реєстрі СЕА (напр. оформлена за договором комісії)">ні · не враховується</span> : <span className="badge badge-idle">СЕА не синхр.</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot><tr><th colSpan={3}>Разом ({counted.length}{off ? `; без ${off} не в СЕА` : ""})</th><th className="num">{sum("value_total")}</th>
                  <th className="num">{sum("duty_total")}</th><th className="num">{sum("vat_total")}</th><th /></tr></tfoot>
              </table>
            </div>
          )}
      </div>
    </>
  );
}

// Копіювання рядків специфікації для майстра ПН («Вставити з таблиці»): назва · код · 1 (імпорт) · одиниця · к-сть · митна ціна
function CopyForPn({ goods, label, markup = 0 }) {
  const [msg, setMsg] = useState(null);
  const copy = async () => {
    const rows = pnRows(goods, { markup });
    const how = Number(markup) ? `ціна — митна з націнкою ${markup}%` : "ціна — митна, без націнки";
    try { await navigator.clipboard.writeText(rows.join("\n")); setMsg(`Скопійовано рядків: ${rows.length} (${how}). У майстрі ПН: «Вставити з таблиці» → вставте.`); }
    catch { setMsg("Не вдалося скопіювати — браузер не дав доступу до буфера обміну."); }
  };
  return (
    <span className="copy-pn">
      <button type="button" className="btn btn-secondary btn-sm" onClick={copy}>{label}</button>
      {msg && <span className="muted small" role="status">{msg}</span>}
    </span>
  );
}

function Declaration({ companyId, mrn, canEdit }) {
  const r = useResource(`/api/companies/${companyId}/customs/${encodeURIComponent(mrn)}`);
  const [error, setError] = useState(null);
  const remove = async () => {
    if (!confirm(`Видалити декларацію ${mrn} з eCabinet? У кабінеті митниці вона лишиться.`)) return;
    try { await api(`/api/companies/${companyId}/customs/${encodeURIComponent(mrn)}`, { method: "DELETE" }); location.hash = `#/c/${companyId}/customs`; }
    catch (e) { setError(e.message); }
  };
  const d = r.data?.declaration;
  // Націнка до митної ціни для рядків ПН — одна на всю декларацію; 0 — копіюємо митну ціну як є
  const [markupText, setMarkupText] = useState("0");
  const markupNum = Number(String(markupText).replace(",", "."));
  const markupOk = markupText.trim() !== "" && Number.isFinite(markupNum) && markupNum >= 0 && markupNum <= 1000;
  const markup = markupOk ? markupNum : 0;
  return (
    <>
      <p className="crumbs"><a href={`#/c/${companyId}/customs`}>← Митні декларації</a></p>
      {r.pending ? <p className="muted" role="status">Завантаження…</p>
        : r.error ? <p className="error">{r.error.message}</p> : (
          <>
            <div className="section-head">
              <div><h2 className="mono-head">{d.mrn}</h2><p className="muted">{d.type} · {d.filename}</p></div>
              {canEdit && <button type="button" className="btn btn-ghost btn-sm" onClick={remove}>Видалити з eCabinet</button>}
            </div>
            {error && <p className="error" role="alert">{error}</p>}
            <div className="card">
              <dl className="pairs">
                <div><dt>Статус</dt><dd>{STATUS[d.status] || d.status || "—"}</dd></div>
                <div><dt>Подано</dt><dd>{anyDay(d.submitted)}</dd></div>
                <div><dt>Оформлено</dt><dd>{anyDay(d.registered)}</dd></div>
                <div><dt>Митна вартість з митом (база ПДВ)</dt><dd>{uah(d.value_total)}</dd></div>
                <div><dt>Мито</dt><dd>{uah(d.duty_total)}</dd></div>
                <div><dt>ПДВ (ввезення)</dt><dd>{uah(d.vat_total)}</dd></div>
                <div><dt>У реєстрі СЕА</dt><dd>{d.in_sea ? `так${Number(d.sea_vat) > 0 ? ` · ПДВ ${money(d.sea_vat)}` : ""}` : "ні"}</dd></div>
                {d.sea_docnum && <div><dt>Номер у СЕА</dt><dd>{d.sea_docnum}</dd></div>}
                <div><dt>Товарів</dt><dd>{r.data.goods.length}</dd></div>
              </dl>
              {r.data.goods.some((g) => g.positions?.length) && (
                <div className="actions copy-pn-bar">
                  <label className="pn-bulk-rate">Націнка, %
                    <input type="text" inputMode="decimal" className="markup-input" value={markupText} onChange={(e) => setMarkupText(e.target.value)}
                      aria-invalid={markupOk ? undefined : true} aria-describedby="markup-hint" />
                  </label>
                  <CopyForPn goods={r.data.goods} label="Копіювати всі позиції для ПН" markup={markup} />
                  <span id="markup-hint" className={markupOk ? "muted small" : "error small"}>{markupOk
                    ? "Діє на всі кнопки «Копіювати для ПН» на цій сторінці: ціна в ПН = митна ціна × (1 + націнка / 100)."
                    : "Вкажіть націнку числом від 0 до 1000 — поки що копіюємо без націнки."}</span>
                </div>
              )}
            </div>
            {r.data.goods.map((g) => {
              const total = (g.positions || []).reduce((a, p) => a + (Number(p.qty) || 0) * (Number(p.price) || 0), 0);
              const uahPos = positionsUah(g);
              return (
                <section key={g.item_no} className="card">
                  <h3 className="block-title">Товар {g.item_no} · УКТЗЕД <a href={`#/c/${companyId}/reference/uktzed/${g.uktzed}`} className="mono-sm">{g.uktzed}</a>
                    {g.risky && <span className="badge badge-warn">перелік ДПС № 67</span>}</h3>
                  {g.ref_short && <p className="muted small">{g.ref_short}</p>}
                  <p className="receipt-text">{g.description}</p>
                  <dl className="pairs">
                    <div><dt>Країна</dt><dd>{g.country || "—"}</dd></div>
                    <div><dt>Вага нетто / брутто</dt><dd>{g.net_weight ?? "—"} / {g.gross_weight ?? "—"} кг</dd></div>
                    <div><dt>К-сть (специфікація)</dt><dd>{g.qty ?? "—"}</dd></div>
                    <div><dt>Митна вартість з митом</dt><dd>{uah(g.customs_value)}</dd></div>
                    <div><dt>Мито</dt><dd>{uah(g.duty)}</dd></div>
                    <div><dt>ПДВ</dt><dd>{uah(g.vat)}</dd></div>
                  </dl>
                  {g.positions?.length > 0 && (
                    <>
                      <div className="row"><h4>Специфікація</h4><CopyForPn goods={[g]} label="Копіювати для ПН" markup={markup} /></div>
                      <p className="muted small">Митна ціна — митна вартість товару разом із митом (база ПДВ, у гривні), розподілена між позиціями пропорційно
                        фактурній вартості, на одиницю. Саме її «Копіювати для ПН» ставить у рядки: назва · код · 1 (імпорт) · одиниця · кількість · ціна.</p>
                      <div className="table-wrap">
                        <table className="table-static mini">
                          <thead><tr><th>#</th><th>Найменування</th><th className="num">К-сть</th><th className="num">Ціна, валюта</th><th className="num">Вартість, валюта</th>
                            <th className="num">Митна ціна, грн</th><th className="num">Митна вартість, грн</th>
                            {markup > 0 && <th className="num">Ціна з націнкою {markup}%, грн</th>}</tr></thead>
                          <tbody>{g.positions.map((p, i) => (
                            <tr key={p.pos}><td>{p.pos}</td><td className="wrap-cell" title={p.name}>{cleanName(p.name, p.qty)}</td><td className="num">{p.qty} {unit(p.unit)}</td>
                              <td className="num muted">{money(p.price)}</td><td className="num muted">{money((Number(p.qty) || 0) * (Number(p.price) || 0))}</td>
                              <td className="num"><b>{uahPos[i]?.priceUah == null ? "—" : money(uahPos[i].priceUah)}</b></td><td className="num">{money(uahPos[i]?.value)}</td>
                              {markup > 0 && <td className="num"><b>{uahPos[i]?.priceUah == null ? "—" : money(withMarkup(uahPos[i].priceUah, markup))}</b></td>}</tr>
                          ))}</tbody>
                          <tfoot><tr><th colSpan={4}>Разом</th><th className="num muted">{money(total)}</th><th /><th className="num">{money(g.customs_value)}</th>{markup > 0 && <th />}</tr></tfoot>
                        </table>
                      </div>
                    </>
                  )}
                </section>
              );
            })}
          </>
        )}
    </>
  );
}

const addDays = (iso, n) => new Date(Date.parse(iso) + n * 86_400_000).toISOString().slice(0, 10);
// Отримати МД з митниці за токеном (сервер: перелік за період → нові оформлені МД → імпорт, як файл .imfx); range — [from, to]
function FetchFromCustoms({ companyId, onDone, label, range }) {
  const [busy, setBusy] = useState(false), [res, setRes] = useState(null), [err, setErr] = useState(null);
  const go = async () => {
    setBusy(true); setErr(null); setRes(null);
    try {
      const body = range ? { from: range[0], to: range[1] } : {};
      const r = await api(`/api/companies/${companyId}/customs-api/fetch`, { method: "POST", body });
      setRes(r); onDone?.();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="customs-fetch">
      <button type="button" className="btn btn-sm" disabled={busy} aria-busy={busy} onClick={go}>{busy ? "Отримуємо з митниці…" : label}</button>
      {res && <p className="small" role="status">{res.imported.length
        ? <span className="pn-ok">✓ Отримано {res.imported.length} МД: {res.imported.map((x) => `${x.mrn} (${x.goods} тов.)`).join(", ")}</span>
        : <span>Нових МД за {anyDay(res.from)}–{anyDay(res.to)} немає (у митниці {res.listed}, уже тут {res.already}).</span>}
        {res.skipped.length > 0 && <span className="muted"> Пропущено: {res.skipped.map((x) => `${x.mrn || "?"} — ${x.reason}`).join("; ")}.</span>}
        {res.more > 0 && <span className="muted"> Ще {res.more} — натисніть ще раз.</span>}</p>}
      {err && <p className="error small" role="alert">{err}</p>}
    </div>
  );
}
